<template>
  <div class="w-full flex justify-content-center">
    <div class="w-10">
      <div>
        <p>Filtrar resultados</p>
        <div class="flex gap-2 align-items-center justify-content-between w-5">
          <Dropdown
            v-model="selectedFilter"
            :options="filterOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Filtro"
            class="custom-focus"
          />
          <Dropdown
            v-model="selectedOperator"
            :options="operatorOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Operador"
            class="custom-focus"
          />
          <InputText v-model="filterValue" placeholder="Valor" class="custom-focus" />
          <Button class="search-button flex-grow-0" @click="onSearch(getFilterQuery())">
            <Search color="white" :size="20" />
            Buscar
          </Button>
          <Button class="clean-button flex-grow-0" @click="onClearFilters">
            <BrushCleaning color="#0B5195" :size="20" />
            Limpiar
          </Button>
        </div>
      </div>

      <DataTable
        :value="blockedUsers"
        :loading="isLoading"
        :first="offset"
        :rows="pageSize"
        :total-records="blockedUsers.length"
        @page="handlePageChange"
        :resizable-columns="false"
        :paginator="true"
        class="center-screen mt-5 shadow-2"
        paginator-template="PrevPageLink PageLinks NextPageLink"
        style="border-radius: 6px"
      >
        <template #empty> Aún no hay usuarios bloqueados. </template>

        <Column field="Email" header="Email" />
        <Column field="NombreUsuario" header="Nombre Usuario" />
        <Column field="TokenFechaCreacion" header="Fecha de Bloqueo" />

        <Column header="Acción">
          <template #body="{ data }">
            <div class="w-full flex justify-content-center">
              <Button class="unlock-button flex-grow-0" @click="showUnlockUser(data)">
                <ShieldCheck color="white" :size="20" />
                Desbloquear
              </Button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <CustomModal
      ref="userModalRef"
      :title="userModalData.title"
      :message="userModalData.message"
      :modal-type="userModalData.modalType"
      :show-secondary-button="userModalData.showSecondaryButton"
      :button-labels="userModalData.buttonLabels"
      @confirm="userModalData.onConfirm"
      @cancel="userModalData.onCancel"
      :loading="isLoading"
    >
      <template v-slot:primaryButton="{ loading, buttonLabels, onConfirm }">
        <button :disabled="loading" @click="onConfirm">
          {{ buttonLabels[0] }}
          <span v-if="loading" class="spinner"></span>
        </button>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import axios from "axios";
import { ShieldCheck, Search, BrushCleaning } from "lucide-vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";

const blockedUsers = ref([]);
const originalUsers = ref([]);
const isLoading = ref(false);
const error = ref(null);

const offset = ref(0);
const pageSize = ref(10);

const FILTERS = [
  { label: "Nombre de Usuario", value: "NombreUsuario" },
  { label: "Email", value: "Email" },
];

const OPERATORS = [
  { label: "IGUAL", value: "eq" },
  { label: "DISTINTO", value: "neq" },
  { label: "CONTIENE", value: "contains" },
  { label: "NO CONTIENE", value: "not_contains" },
];

const selectedFilter = ref(FILTERS[0].value);
const selectedOperator = ref(OPERATORS[0].value);
const filterValue = ref("");

const filterOptions = FILTERS;
const operatorOptions = OPERATORS;

function getFilterQuery() {
  return {
    field: selectedFilter.value,
    operator: selectedOperator.value,
    value: filterValue.value,
  };
}

const userModalRef = ref(null);
const userModalData = ref({
  title: "",
  message: "",
  modalType: "",
  showSecondaryButton: true,
  buttonLabels: [],
  onConfirm: () => {},
  onCancel: () => {},
});

function formatDate(dateString) {
  if (!dateString) return "";

  const date = new Date(dateString.replace(" ", "T"));

  const dia = String(date.getDate()).padStart(2, "0");
  const mes = String(date.getMonth() + 1).padStart(2, "0");
  const año = date.getFullYear();

  const hora = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");

  return `${dia}/${mes}/${año} ${hora}:${min} Hs`;
}

const fetchblockedUsers = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const res = await axios.get(
      `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/usuario/?search={"Intentos":[{"operator":">","value":"2"}]}`,
      { withCredentials: true }
    );

    originalUsers.value = res.data.data.map((user) => ({
      UsuarioID: user.UsuarioID,
      Email: user.Email || "",
      NombreUsuario: user.NombreUsuario || "",
      TokenFechaCreacion: formatDate(user.TokenFechaCreacion),
    }));

    blockedUsers.value = [...originalUsers.value];
  } catch (err) {
    error.value = err.message;
    console.error("Error al obtener usuarios:", err);
  } finally {
    isLoading.value = false;
  }
};

const unlockUser = async (idUsuario) => {
  return axios.patch(
    `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/usuario/${idUsuario}/desbloquear`,
    {},
    { withCredentials: true }
  );
};

const showUnlockUser = (data) => {
  userModalData.value = {
    title: "Desbloqueo de usuario",
    message:
      "¿Está seguro de que desea desbloquear este usuario? Esta acción permitirá que el usuario vuelva a acceder al sistema.",
    modalType: "warning",
    showSecondaryButton: true,
    buttonLabels: ["Desbloquear", "Cancelar"],
    onConfirm: async () => {
      isLoading.value = true;
      userModalData.value.buttonLabels[0] = "Desbloqueando...";

      try {
        await unlockUser(data.UsuarioID);
        await fetchblockedUsers();
        userModalRef.value.close();

        userModalData.value = {
          title: "Desbloqueo exitoso",
          message:
            "El usuario fue desbloqueado con éxito. Ya podés comunicarle que puede volver a ingresar al sistema.",
          modalType: "success",
          showSecondaryButton: false,
          buttonLabels: ["Aceptar"],
          onConfirm: () => userModalRef.value.close(),
        };
        userModalRef.value.open();
      } catch (err) {
        console.error("Error al desbloquear:", err);

        userModalData.value = {
          title: "Error al desbloquear usuario",
          message: "Ocurrió un problema durante el proceso de desbloqueo. Intente nuevamente.",
          modalType: "warning",
          showSecondaryButton: false,
          buttonLabels: ["Cerrar"],
          onConfirm: () => userModalRef.value.close(),
        };

        userModalRef.value.open();
      } finally {
        isLoading.value = false;
        if (userModalData.value.modalType === "warning") {
          userModalData.value.buttonLabels[0] = "Desbloquear";
        }
      }
    },
    onCancel: () => {
      userModalRef.value.close();
    },
  };

  userModalRef.value.open();
};

const handlePageChange = (event) => {
  offset.value = event.first;
  pageSize.value = event.rows;
};

onMounted(fetchblockedUsers);

function applyFilter(data, field, operator, value) {
  return data.filter((user) => {
    const fieldValue = (user[field] || "").toString().toLowerCase();
    const searchValue = (value || "").toLowerCase();

    switch (operator) {
      case "eq":
        return fieldValue === searchValue;
      case "neq":
        return fieldValue !== searchValue;
      case "contains":
        return fieldValue.includes(searchValue);
      case "not_contains":
        return !fieldValue.includes(searchValue);
      default:
        return true;
    }
  });
}

function onSearch(filterQuery) {
  if (!filterQuery || !filterQuery.value) {
    blockedUsers.value = [...originalUsers.value];
    return;
  }
  blockedUsers.value = applyFilter(
    originalUsers.value,
    filterQuery.field,
    filterQuery.operator,
    filterQuery.value
  );
}

function onClearFilters() {
  offset.value = 0;
  pageSize.value = 10;
  fetchblockedUsers();
}
</script>

<style scoped>
.clean-button {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-content: center !important;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background: var(--White-White, #fff);
  color: #0b5195;
  border: 1px solid #0b5195;
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4);
}

.clean-button:hover {
  background: #f4f4f4 !important;
  color: #0b5195 !important;
  opacity: 0.9;
  cursor: pointer;
}

.search-button {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-content: center !important;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background: #0b5195;
  color: #ffffff;
  border: 1px solid #0b5195;
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4);
}

.search-button:hover {
  background: #0b5195 !important;
  color: #f4f4f4 !important;
  opacity: 0.9;
  cursor: pointer;
}

.unlock-button {
  display: flex;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-content: center !important;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background: #0b5195;
  color: #ffffff;
  border: none;
}

.unlock-button:hover {
  opacity: 0.9;
  cursor: pointer;
}

.center-screen {
  margin: 0 auto 1rem auto;
}

.border-gray {
  border: 1px solid #dfe4ea;
}

.selected {
  background-color: #3758f9;
  color: white;
}

.text-custom-gray {
  color: #637381;
}

.custom-focus:focus-within {
  border: 1.5px solid #3758f9;
}

>>> .p-datatable .p-column-header-content {
  justify-content: center;
}

>>> .p-datatable .p-datatable-tbody > tr > td {
  text-align: center;
}

>>> .p-datatable-table {
  table-layout: fixed;
}

>>> .p-datatable-wrapper .p-datatable-table {
  min-height: 100%;
}

>>> .p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
  background: #3758f9;
  color: #fff;
}

>>> .p-link {
  border-radius: 6px !important;
  border: 1px solid #dfe4ea !important;
}
</style>
